import { type LoaderFunctionArgs, redirect } from '@remix-run/node'
import {
	Link,
	type MetaFunction,
	defer,
	useLoaderData,
	isRouteErrorResponse,
	useRouteError,
} from '@remix-run/react'
import {
	getAttestationv2,
	getNFTFromCache,
	getRollupForDerivedAttestation,
} from '#app/server/db.server.ts'
import MetadataProfile from '#app/components/metadataProfile/metadataProfile.tsx'
import { AttestationType, nft, NFTCache } from '#types/loreTypes.tsx'
import Button from '#app/components/buttons/button'
import Section from '#app/components/layout/section'
import TypographyParagraph from '#app/components/typography/TypographyParagraph'
import { captureRemixErrorBoundaryError } from '@sentry/remix'
import generateMetaProfileMeta from '#app/utils/meta/metadataProfileMeta'
import { getDomainUrl } from '#app/utils/misc.server'

export const meta: MetaFunction<typeof loader> = ({
	data,
	params,
	location,
}) => {
	const nftData = data?.nftData
	const origin = data?.origin
	const { pathname } = location
	return generateMetaProfileMeta({
		origin,
		path: pathname,
		nft: nftData || undefined,
	})
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const origin = getDomainUrl(request)
	if (params.cid && params.chain && params.contractAddress && params.tokenId) {
		let rollupAttestation: AttestationType | undefined = undefined
		let nftData: NFTCache | undefined = undefined
		let metadata: AttestationType | undefined = undefined
		try {
			const nftDataResponse = await getNFTFromCache(
				params.chain,
				params.contractAddress,
				params.tokenId,
			)
			if (nftDataResponse) {
				nftData = nftDataResponse
			}
		} catch (e) {
			console.error('ERROR RETRIEVING NFT FROM CACHE', {
				e,
				chain: params.chain,
				contractAddress: params.contractAddress,
				tokenId: params.tokenId,
			})
			throw new Response('Error retrieving NFT', {
				status: 500,
			})
		}
		try {
			let returnedMetadata
			const retrievedAttestation = await getAttestationv2({
				attestationIPFSCID: params.cid,
			})
			if (
				retrievedAttestation &&
				retrievedAttestation.attestations &&
				retrievedAttestation.attestations.length
			) {
				returnedMetadata = retrievedAttestation.attestations[0]
			}
			if (!returnedMetadata) {
				console.error('NO RETRIEVED ATTESTATION For Metadata Profile', {
					chain: params.chain,
					contractAddress: params.contractAddress,
					tokenId: params.tokenId,
					cid: params.cid,
				})
				return redirect(
					`/nftProfile/${params.chain}/${params.contractAddress}/${params.tokenId}`,
				)
			} else {
				metadata = returnedMetadata
				if (metadata.properties.eventCID) {
					// Fetch related rollup attestation
					rollupAttestation = await getRollupForDerivedAttestation(params.cid)
				}
			}
		} catch (e) {
			console.error('ERROR RETRIEVING Attestation For Metadata Profile', {
				e,
				chain: params.chain,
				contractAddress: params.contractAddress,
				tokenId: params.tokenId,
				cid: params.cid,
			})
			throw new Response('Error retrieving attestation!', {
				status: 500,
			})
		}
		return defer({
			nftData,
			atomicLoreUrl: url.origin,
			cid: params.cid,
			metadataEntry: metadata,
			rollupAttestation,
			origin,
		})
	}
}

export default function metadataPage() {
	const { nftData, cid, metadataEntry, rollupAttestation, atomicLoreUrl } =
		useLoaderData<{
			nftData: nft
			cid: string
			metadataEntry: AttestationType
			rollupAttestation: AttestationType
			atomicLoreUrl: string
		}>()
	return (
		<MetadataProfile
			nftData={nftData}
			cid={cid}
			metadataEntry={metadataEntry}
			rollupAttestation={rollupAttestation}
			atomicLoreUrl={atomicLoreUrl}
		/>
	)
}

export function ErrorBoundary() {
	const error = useRouteError()
	console.error('Error is:', error)
	captureRemixErrorBoundaryError(error)
	if (isRouteErrorResponse(error)) {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Status: {error.status} - {error.statusText}
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Data: {error.data}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else if (error instanceof Error) {
		console.error('Error is:', error)
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An error occurred while loading this Attestation
				</TypographyParagraph>
				<TypographyParagraph size="large" additionalClasses="pt-2 pb-4">
					Error Message: {error.message}
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	} else {
		return (
			<Section title={'Error Loading Attestation'}>
				<TypographyParagraph size="large">
					An unknown error occurred while loading this Attestation
				</TypographyParagraph>
				<Link to="/" prefetch="none">
					<Button>Go Home</Button>
				</Link>
			</Section>
		)
	}
}
